import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Tool, ToolsState, ToolCategory } from "../../types/tools";

// ─── localStorage helpers ────────────────────────────────────────────────────

const FAVORITES_KEY = "tools_favorites";
const RECENTLY_USED_KEY = "tools_recently_used";

const loadArray = (key: string): string[] => {
    try {
        const raw = localStorage.getItem(key);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) return parsed;
        }
    } catch (_) {
        /* ignore */
    }
    return [];
};

const saveArray = (key: string, value: string[]) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (_) {
        /* ignore */
    }
};

const initialState: ToolsState = {
    tools: [],
    recentlyUsed: loadArray(RECENTLY_USED_KEY),
    favorites: loadArray(FAVORITES_KEY),
    searchQuery: "",
    selectedCategory: "all",
    isLoading: false,
    error: null,
};

// Async thunks
export const loadTools = createAsyncThunk("tools/loadTools", async () => {
    // For now, return static tools. In future, this could fetch from API
    const tools: Tool[] = [
        {
            id: "price-list-generator",
            name: "Price List Generator",
            description:
                "Generate formatted price lists for social media and customer service",
            category: "customer_service",
            status: "active",
            icon: "PriceCheck",
            path: "/tools/price-list-generator",
        },
        {
            id: "delivery-planner",
            name: "Delivery Planner",
            description:
                "Plan and prioritize upcoming deliveries with drag-and-drop ordering",
            category: "utilities",
            status: "active",
            icon: "LocalShipping",
            path: "/tools/delivery-planner",
        },
        {
            id: "daily-cash-flow-analyzer",
            name: "Cash Flow Analyzer",
            description:
                "Combine offline sales, in-house delivery collections, expenses and supplier payments into a cash flow report for a selected date range",
            category: "reports",
            status: "active",
            icon: "Payment",
            path: "/tools/daily-cash-flow-analyzer",
        },
        {
            id: "stock-alert-messenger",
            name: "Stock Alert Messenger",
            description:
                "Generate formatted messages for low stock alerts to suppliers",
            category: "inventory",
            status: "coming_soon",
            icon: "Inventory",
            path: "/tools/stock-alert-messenger",
        },
        {
            id: "customer-birthday-reminder",
            name: "Customer Birthday Reminder",
            description:
                "View upcoming customer birthdays and generate promotional messages",
            category: "customer_service",
            status: "coming_soon",
            icon: "Cake",
            path: "/tools/customer-birthday-reminder",
        },
        {
            id: "order-summary-generator",
            name: "Order Summary Generator",
            description:
                "Create daily/weekly order summaries for management reports",
            category: "reports",
            status: "coming_soon",
            icon: "Summarize",
            path: "/tools/order-summary-generator",
        },
        {
            id: "supplier-payment-tracker",
            name: "Supplier Payment Tracker",
            description:
                "Track pending payments and generate payment reminder messages",
            category: "utilities",
            status: "coming_soon",
            icon: "Payment",
            path: "/tools/supplier-payment-tracker",
        },
        {
            id: "product-performance-analyzer",
            name: "Product Performance Analyzer",
            description:
                "Quick insights on best/worst selling products with shareable reports",
            category: "analytics",
            status: "coming_soon",
            icon: "Analytics",
            path: "/tools/product-performance-analyzer",
        },
        {
            id: "customer-segment-messenger",
            name: "Customer Segment Messenger",
            description:
                "Generate targeted messages for customer segments (VIP, inactive, etc.)",
            category: "customer_service",
            status: "coming_soon",
            icon: "Groups",
            path: "/tools/customer-segment-messenger",
        },
        {
            id: "inventory-reorder-calculator",
            name: "Inventory Reorder Calculator",
            description: "Calculate reorder quantities based on sales velocity",
            category: "inventory",
            status: "coming_soon",
            icon: "Calculate",
            path: "/tools/inventory-reorder-calculator",
        },
        {
            id: "promotion-code-generator",
            name: "Promotion Code Generator",
            description: "Create and manage promotional discount codes",
            category: "utilities",
            status: "coming_soon",
            icon: "LocalOffer",
            path: "/tools/promotion-code-generator",
        },
    ];
    return tools;
});

export const recordToolUsage = createAsyncThunk(
    "tools/recordUsage",
    async (toolId: string) => {
        // In future, this could save to API/localStorage
        return toolId;
    },
);

const toolsSlice = createSlice({
    name: "tools",
    initialState,
    reducers: {
        setSearchQuery: (state, action: PayloadAction<string>) => {
            state.searchQuery = action.payload;
        },
        setSelectedCategory: (
            state,
            action: PayloadAction<ToolCategory | "all">,
        ) => {
            state.selectedCategory = action.payload;
        },
        addToFavorites: (state, action: PayloadAction<string>) => {
            if (!state.favorites.includes(action.payload)) {
                state.favorites.push(action.payload);
                saveArray(FAVORITES_KEY, state.favorites);
            }
        },
        removeFromFavorites: (state, action: PayloadAction<string>) => {
            state.favorites = state.favorites.filter(
                (id) => id !== action.payload,
            );
            saveArray(FAVORITES_KEY, state.favorites);
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Load tools
            .addCase(loadTools.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loadTools.fulfilled, (state, action) => {
                state.isLoading = false;
                state.tools = action.payload;
            })
            .addCase(loadTools.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || "Failed to load tools";
            })
            // Record tool usage
            .addCase(recordToolUsage.fulfilled, (state, action) => {
                const toolId = action.payload;
                // Add to recently used (keep last 5)
                state.recentlyUsed = [
                    toolId,
                    ...state.recentlyUsed.filter((id) => id !== toolId),
                ].slice(0, 5);
                saveArray(RECENTLY_USED_KEY, state.recentlyUsed);

                // Update usage count
                const tool = state.tools.find((t) => t.id === toolId);
                if (tool) {
                    tool.usageCount = (tool.usageCount || 0) + 1;
                    tool.lastUsed = new Date().toISOString();
                }
            });
    },
});

export const {
    setSearchQuery,
    setSelectedCategory,
    addToFavorites,
    removeFromFavorites,
    clearError,
} = toolsSlice.actions;

export default toolsSlice.reducer;
